import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EChart from '../Cost/EChart'
import { fmtCount } from '../StreamIngest/vocabulary'
import { PUSH_KINDS, fmtShare, share } from './vocabulary'
import {
  END_LABEL_LAYOUT,
  TOOLTIP,
  axisStyle,
  useChartStyle,
} from './chartStyle'

// Each kind of push's share that led to a listen, by the UTC day it was sent
// (the pushes read's `by_day`): whether a kind is getting better or worse,
// which the bars above, a window's total, cannot say. A line per kind, in the
// kind's own colour whatever else is drawn, its name at its end; a day it sent
// nothing to a reporting phone is a gap, never a 0%. The tooltip carries the
// counts behind each share: on a day of three pushes, one listen is 33%.

const KINDS = Object.keys(PUSH_KINDS)

// A kind's name at the end of its line, short enough to sit beside it.
const SHORT = {
  stream_moment: 'own quote',
  friend_quotes: 'friends’ quotes',
  comment: 'comment',
  friend_request: 'request',
  friend_accepted: 'accepted',
}

const daysOf = (first, last) => {
  const out = []
  for (let day = new Date(`${first}T00:00:00Z`); ; ) {
    const iso = day.toISOString().slice(0, 10)
    out.push(iso)
    if (iso >= last) return out
    day = new Date(day.getTime() + 86400000)
  }
}

const PushTrend = ({ answer }) => {
  const style = useChartStyle()
  const { days, series, rows } = useMemo(() => {
    const rows = {}
    ;(answer?.by_day || []).forEach((row) => {
      rows[`${row.kind}|${row.day}`] = row
    })
    const days = answer ? daysOf(answer.first, answer.last) : []
    const present = new Set((answer?.by_day || []).map(({ kind }) => kind))
    const kinds = [
      ...KINDS.filter((kind) => present.has(kind)),
      ...[...present].filter((kind) => !KINDS.includes(kind)).sort(),
    ]
    const series = kinds.map((kind) => ({
      kind,
      data: days.map((day) => {
        const row = rows[`${kind}|${day}`]
        return row && row.counted
          ? Math.round((row.heard / row.counted) * 100)
          : null
      }),
    }))
    return { days, series, rows }
  }, [answer])

  const option = useMemo(
    () => ({
      grid: { left: 44, right: 112, top: 16, bottom: 24 },
      tooltip: {
        trigger: 'axis',
        ...TOOLTIP,
        formatter: (points) => {
          const day = days[points[0]?.dataIndex]
          const lines = points
            .map(({ seriesIndex, marker }) => {
              const { kind } = series[seriesIndex]
              const row = rows[`${kind}|${day}`]
              if (!row || !row.counted) return null
              return `${marker}${PUSH_KINDS[kind] || kind}: ${fmtCount(
                row.heard
              )} of ${fmtCount(row.counted)} (${fmtShare(
                share(row.heard, row.counted)
              )})`
            })
            .filter(Boolean)
          return `<div style="margin-bottom:4px">${day} UTC</div>${
            lines.join('<br/>') || 'no push to a reporting phone'
          }`
        },
      },
      xAxis: {
        type: 'category',
        data: days,
        ...axisStyle(style),
        axisLabel: {
          ...axisStyle(style).axisLabel,
          formatter: (d) => d.slice(5),
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        ...axisStyle(style),
        axisLabel: { ...axisStyle(style).axisLabel, formatter: (v) => `${v}%` },
      },
      series: series.map(({ kind, data }) => {
        const slot = KINDS.indexOf(kind)
        const color = slot >= 0 ? style.series[slot] : style.muted
        return {
          name: PUSH_KINDS[kind] || kind,
          type: 'line',
          data,
          connectNulls: false,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 2, color },
          itemStyle: { color },
          labelLayout: END_LABEL_LAYOUT,
          endLabel: {
            show: true,
            formatter: () => SHORT[kind] || kind,
            color: style.text,
            fontSize: 11,
          },
        }
      }),
    }),
    [days, series, rows, style]
  )

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Led to a listen, by day sent
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        Each kind’s share of its pushes to a reporting phone. Hover a day for
        the counts behind it.
      </Typography>
      <EChart
        option={option}
        height={240}
        isEmpty={days.length < 2 || series.length === 0}
        emptyText={
          days.length < 2
            ? 'Pick 7d or more to see the days.'
            : 'No push to a reporting phone in this window.'
        }
      />
    </Box>
  )
}

export default PushTrend
